// WHAT: Generic status card component for displaying a primary status and a list of key/value items.
// WHY:  Shared building block for token/status overviews (e.g. Withings TokenTab) using the global Card styling.
// DOMAIN: Shared UI → Status & Summary

import type { FC, ReactNode } from "react";
import clsx from "clsx";

import { Card } from "./Card";

export interface StatusCardStatus {
  /**
   * Visual variant identifier.
   * Consumers can choose any string; this component itself does not hard‑code colors by variant
   * to keep color responsibility in the global Card/theme system.
   */
  variant: string;
  /**
   * Human readable status label, e.g. "Active", "Expired".
   */
  label: ReactNode;
  /**
   * Optional icon element rendered before the label.
   */
  icon?: ReactNode;
}

export interface StatusCardItem {
  /**
   * Label for the row (e.g. "Expires", "Remaining").
   */
  label: ReactNode;
  /**
   * Value for the row. Can be simple text or a React element (e.g. TimeRemaining).
   */
  value: ReactNode;
  /**
   * Optional icon rendered before the value.
   */
  icon?: ReactNode;
}

export interface StatusCardProps {
  /**
   * Title shown in the card header.
   */
  title: ReactNode;
  /**
   * Primary status information (label + optional icon).
   */
  status: StatusCardStatus;
  /**
   * Detail rows rendered as label/value pairs.
   */
  items: StatusCardItem[];
  /**
   * Additional CSS classes forwarded to the underlying Card.
   */
  className?: string;
}

export const StatusCard: FC<StatusCardProps> = ({ title, status, items, className }) => {
  return (
    <Card
      className={clsx(
        "flex flex-col gap-3", // layout only, colors come from global Card
        className,
      )}
      header={
        <div className="flex items-center justify-between gap-3">
          <div className="text-headline text-tracq-deep-blue font-semibold">{title}</div>
          <div className="inline-flex items-center gap-2 text-body-sm text-gray-300">
            {status.icon}
            <span>{status.label}</span>
          </div>
        </div>
      }
    >
      <dl className="mt-1 space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex items-center justify-between gap-3 text-body-sm">
            <dt className="text-gray-400">{item.label}</dt>
            <dd className="flex items-center gap-2 text-gray-200">
              {item.icon}
              {item.value}
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
};
